import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, Award, PencilLine, PenTool, Copy, Building2, Upload, CheckCircle2, Lock } from "lucide-react";
import SignaturePad from "../../components/signature/SignaturePad";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABELS = {
  visura_catastale: "Visura Catastale",
  planimetria_catastale: "Planimetria Catastale",
  ape: "Attestato di Prestazione Energetica",
  contratto_locazione: "Contratto di locazione",
  scheda_iscrizione: "Scheda d'iscrizione",
  documento_cf_locatore: "Documento CF — Locatore",
  documento_cf_conduttore: "Documento CF — Conduttore",
  ricevuta_bonifico: "Ricevuta bonifico",
  doc_transitorieta: "Doc. transitorietà",
  doc_studenti: "Iscrizione universitaria",
  attestazione_bilaterale: "Attestazione Bilaterale (timbrata)",
};

const STATUS_LABEL = {
  bozza: "Bozza", inviata: "Inviata", in_verifica: "In verifica",
  documenti_mancanti: "Documenti mancanti",
  contratto_approvato: "Firma abilitata",
  approvata: "Approvata",
  attestazione_inviata: "Attestazione inviata", respinta: "Respinta",
};

// Statuses in which the digital signature of the contract is authorised.
// Must stay in sync with backend models.SIGNABLE_STATUSES.
const SIGNABLE_STATUSES = new Set(["contratto_approvato", "approvata", "attestazione_inviata"]);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", {day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}); }
  catch { return iso; }
};

export default function UserPracticeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  // Detect whether this detail page is being accessed from the Agenzia panel
  // (`/agenzia/pratica/…`) so we can adjust the back-link and auth redirects.
  const location = useLocation();
  const isAgenziaRoute = location.pathname.startsWith("/agenzia/");
  const backHref = isAgenziaRoute ? "/agenzia/dashboard" : "/utente";
  const loginHref = isAgenziaRoute ? "/agenzia/login" : "/utente/login";
  const token = localStorage.getItem("cc_user_token");
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  // ── Firma digitale ──
  const [signLocOpen, setSignLocOpen] = useState(false);
  const [signAgOpen, setSignAgOpen] = useState(false);
  const [locSig, setLocSig] = useState("");
  const [agLocSig, setAgLocSig] = useState("");
  const [agCondSig, setAgCondSig] = useState("");
  const [signing, setSigning] = useState(false);
  const [conduttoreLink, setConduttoreLink] = useState("");
  const [genLinkLoading, setGenLinkLoading] = useState(false);

  // Inline re-upload for "documenti_mancanti" state — key: doc_type, value: bool
  const [reuploading, setReuploading] = useState({});

  const reuploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Solo file PDF sono accettati");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File troppo grande (max 15 MB)");
      return;
    }
    setReuploading((s) => ({ ...s, [docType]: true }));
    try {
      const form = new FormData();
      form.append("doc_type", docType);
      form.append("file", file);
      await axios.post(`${API}/submissions/${id}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_LABELS[docType] || docType} caricato`);
      // Refresh submission
      const r = await axios.get(`${API}/user/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSub(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setReuploading((s) => ({ ...s, [docType]: false }));
    }
  };

  const isAgenzia = (sub?.data?.richiedente?.tipo || "").toLowerCase() === "agenzia";
  const firmeState = sub?.data?.firme || {};
  const locSigned = !!firmeState.firma_locatore;
  const condAttSigned = !!firmeState.firma_conduttore;
  const condContrSigned = !!firmeState.firma_conduttore_contratto;
  const condSigned = condAttSigned && condContrSigned;

  const signLocatore = async () => {
    if (!locSig) { toast.error("Apponi la firma prima di confermare"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/user/submissions/${id}/sign-locatore`, { signature: locSig }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Firma locatore acquisita");
      setSignLocOpen(false);
      setLocSig("");
      // Refresh
      const r = await axios.get(`${API}/user/submissions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSub(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare la firma");
    } finally { setSigning(false); }
  };

  const signAgenzia = async () => {
    if (!agLocSig || !agCondSig) { toast.error("Servono entrambe le firme (locatore e conduttore)"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/user/submissions/${id}/sign-agenzia`,
        { firma_locatore: agLocSig, firma_conduttore: agCondSig },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Firme in sede acquisite");
      setSignAgOpen(false); setAgLocSig(""); setAgCondSig("");
      const r = await axios.get(`${API}/user/submissions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSub(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Firma in sede fallita");
    } finally { setSigning(false); }
  };

  const generateConduttoreLink = async () => {
    setGenLinkLoading(true);
    try {
      const r = await axios.post(`${API}/user/submissions/${id}/generate-conduttore-token`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConduttoreLink(r.data.url);
      toast.success("Link firma conduttore generato (valido 7 giorni)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile generare il link");
    } finally { setGenLinkLoading(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(conduttoreLink);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Copia fallita — seleziona manualmente il link");
    }
  };

  useEffect(() => {
    if (!token) { nav("/utente/login"); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/user/submissions/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSub(r.data);
      } catch (e) {
        if (e?.response?.status === 401) { localStorage.removeItem("cc_user_token"); nav(loginHref); return; }
        toast.error(e?.response?.data?.detail || "Impossibile caricare la pratica");
        nav(backHref);
      } finally { setLoading(false); }
    })();
  }, [id, token, nav]);

  const download = async (fileId, filename, targetPath) => {
    setDownloading(fileId || targetPath);
    try {
      const url = targetPath || `${API}/user/submissions/${id}/files/${fileId}`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename || "download.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fallito");
    } finally { setDownloading(null); }
  };

  const openInForm = async () => {
    try {
      const r = await axios.post(`${API}/user/submissions/${id}/reopen-for-edit`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Hydrate localStorage so FormContext + FormPage restart from this submission.
      localStorage.setItem("cc_form_data", JSON.stringify(r.data.data || {}));
      localStorage.setItem("cc_submission_id", id);
      localStorage.setItem("cc_edit_mode", "1");
      // Restricted-edit mode: expose the admin's allowed steps so the form
      // locks all other sections.
      const steps = r.data?.admin_change_request?.steps_to_edit || [];
      if (Array.isArray(steps) && steps.length > 0) {
        localStorage.setItem("cc_restricted_steps", JSON.stringify(steps));
      } else {
        localStorage.removeItem("cc_restricted_steps");
      }
      toast.success("Pratica caricata nel modulo — puoi modificarla");
      nav("/attestazione");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile aprire la pratica in modifica");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!sub) return null;

  const d = sub.data || {};
  const imm = d.immobile || {};
  const loc = d.locatore || {};
  const cont = d.contratto || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-practice-detail">
      <div className="max-w-4xl mx-auto p-6 lg:p-10">
        <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="user-back-btn">
          <ArrowLeft size={14} /> {isAgenziaRoute ? "Torna alla dashboard Professionista" : "Torna alle mie pratiche"}
        </Link>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mb-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pratica #{sub.id.slice(0,8)}</p>
              <h1 className="text-2xl font-serif text-slate-900 mt-1">{imm.via} {imm.civico}, {imm.citta || "Milano"}</h1>
              <p className="text-sm text-slate-600 mt-1">
                Zona {d.zona || "—"} · {cont.tipologia || "—"} · Locatore: {loc.nome || "—"}
              </p>
            </div>
            <span className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border bg-emerald-50 border-emerald-300 text-emerald-800" data-testid="user-status-badge">
              {STATUS_LABEL[sub.status] || sub.status}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            {sub.submitted_at ? `Inviata il ${fmtDate(sub.submitted_at)}` : `Creata il ${fmtDate(sub.created_at)}`}
          </p>

          {(sub.status === "bozza" || sub.status === "documenti_mancanti") && (
            <div className={`mt-4 border rounded-md p-3 ${
              sub.status === "documenti_mancanti"
                ? "border-orange-300 bg-orange-50"
                : "border-amber-300 bg-amber-50"
            }`} data-testid="user-edit-cta">
              <div className="flex items-start gap-2">
                <PencilLine size={16} className={sub.status === "documenti_mancanti" ? "text-orange-800 shrink-0 mt-0.5" : "text-amber-800 shrink-0 mt-0.5"} />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${sub.status === "documenti_mancanti" ? "text-orange-900" : "text-amber-900"}`}>
                    {sub.status === "documenti_mancanti"
                      ? "Modifiche richieste dall'amministrazione"
                      : "Bozza modificabile"}
                  </p>
                  {sub.status === "documenti_mancanti" && sub.admin_change_request?.note ? (
                    <div className="mt-2 bg-white/70 border border-orange-200 rounded p-2.5" data-testid="user-admin-change-note">
                      <p className="text-[11px] uppercase tracking-wider text-orange-800 font-semibold mb-1">Nota dell&apos;amministrazione</p>
                      <p className="text-xs text-orange-900 whitespace-pre-wrap leading-relaxed">{sub.admin_change_request.note}</p>
                    </div>
                  ) : (
                    <p className={`text-xs mt-0.5 ${sub.status === "documenti_mancanti" ? "text-orange-800" : "text-amber-800"}`}>
                      {sub.status === "documenti_mancanti"
                        ? "Unioncasa ha richiesto integrazioni. La pratica è in analisi: puoi ricaricare solo i documenti segnalati qui sotto, i dati non sono più modificabili."
                        : "Questa pratica non è stata ancora inviata. Puoi tornare al modulo per completarla o modificare i dati."}
                    </p>
                  )}

                  {/* Bozza → link al modulo */}
                  {sub.status === "bozza" && (
                    <button
                      type="button"
                      onClick={openInForm}
                      className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 rounded text-white text-xs font-medium bg-amber-800 hover:bg-amber-900"
                      data-testid="user-open-in-form-btn"
                    >
                      Riprendi il modulo →
                    </button>
                  )}

                  {/* Documenti mancanti → upload inline dei soli doc segnalati */}
                  {sub.status === "documenti_mancanti" && (
                    <div className="mt-3 space-y-2" data-testid="user-reupload-list">
                      {/* Sezioni dati del form autorizzate alla modifica */}
                      {(sub.admin_change_request?.steps_to_edit || []).length > 0 && (
                        <div className="bg-white border border-orange-200 rounded p-3 flex items-start gap-2" data-testid="user-steps-to-edit">
                          <PencilLine size={14} className="text-orange-700 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-orange-900 mb-0.5">Sezioni del form da correggere</p>
                            <p className="text-[11px] text-orange-800 leading-relaxed mb-2">
                              L&apos;admin ha autorizzato la modifica degli step{" "}
                              <strong>{(sub.admin_change_request.steps_to_edit || []).sort((a,b)=>a-b).join(", ")}</strong>.
                              Gli altri step restano bloccati con icona 🔒.
                            </p>
                            <button
                              type="button"
                              onClick={openInForm}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-white text-xs font-medium bg-orange-700 hover:bg-orange-800"
                              data-testid="user-open-restricted-btn"
                            >
                              Apri modulo e correggi dati →
                            </button>
                          </div>
                        </div>
                      )}
                      {(sub.admin_change_request?.docs_to_reupload || []).length > 0 ? (
                        (sub.admin_change_request.docs_to_reupload || []).map((docType) => {
                          const existing = (sub.files || []).find((f) => f.doc_type === docType);
                          const busy = !!reuploading[docType];
                          const inputId = `reupload-${docType}`;
                          return (
                            <div key={docType} className="flex items-center gap-3 bg-white border border-orange-200 rounded p-2.5" data-testid={`reupload-row-${docType}`}>
                              <FileText size={16} className="text-orange-700 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-900">{DOC_LABELS[docType] || docType}</p>
                                <p className="text-[11px] text-slate-500 truncate">
                                  {existing ? (
                                    <>
                                      <CheckCircle2 size={10} className="inline text-emerald-600 mr-0.5" />
                                      Attuale: {existing.filename} ({(existing.size/1024).toFixed(1)} KB)
                                    </>
                                  ) : (
                                    <>Non ancora caricato</>
                                  )}
                                </p>
                              </div>
                              <input
                                id={inputId}
                                type="file"
                                accept="application/pdf"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) reuploadDoc(docType, f);
                                  e.target.value = "";
                                }}
                                data-testid={`reupload-input-${docType}`}
                              />
                              <label
                                htmlFor={inputId}
                                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-white text-[11px] font-medium cursor-pointer ${
                                  busy ? "bg-slate-400 cursor-wait" : "bg-orange-700 hover:bg-orange-800"
                                }`}
                                data-testid={`reupload-btn-${docType}`}
                              >
                                {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                                {busy ? "Caricamento…" : existing ? "Sostituisci" : "Carica PDF"}
                              </label>
                            </div>
                          );
                        })
                      ) : (sub.admin_change_request?.steps_to_edit || []).length > 0 ? null : (
                        <p className="text-xs text-orange-800 italic">
                          L&apos;amministrazione non ha ancora indicato quali documenti ricaricare. Contatta Unioncasa per maggiori dettagli.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Attestazione bilaterale (solo se disponibile) */}
        {sub.attestazione_available && (
          <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-300 rounded-lg p-5 mb-4" data-testid="user-attestazione-card">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
                <Award size={22} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Attestazione Bilaterale disponibile</p>
                <p className="text-xs text-slate-600 mt-0.5">Il documento con il timbro delle associazioni è pronto per il download.</p>
              </div>
              <button
                onClick={() => download(null, `Attestazione_Bilaterale_${sub.id.slice(0,8)}.pdf`, `${API}/user/submissions/${id}/attestazione`)}
                disabled={downloading === `${API}/user/submissions/${id}/attestazione`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-60"
                data-testid="user-attestazione-download">
                <Download size={14} /> Scarica
              </button>
            </div>
          </div>
        )}

        {/* File caricati */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Documenti allegati ({sub.files?.length || 0})
          </div>
          {(!sub.files || sub.files.length === 0) ? (
            <div className="p-6 text-center text-sm text-slate-500">Nessun file caricato per questa pratica.</div>
          ) : (
            sub.files.filter(f => f.doc_type !== "attestazione_bilaterale").map((f) => (
              <div key={f.id} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`user-file-${f.doc_type}`}>
                <FileText size={16} className="text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 truncate">{DOC_LABELS[f.doc_type] || f.doc_type}</p>
                  <p className="text-[11px] text-slate-500">{f.filename} · {(f.size/1024).toFixed(1)} KB{f.generated ? " · generato" : ""}</p>
                </div>
                <button
                  onClick={() => download(f.id, f.filename)}
                  disabled={downloading === f.id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  data-testid={`user-download-${f.doc_type}`}>
                  <Download size={12} /> Scarica
                </button>
              </div>
            ))
          )}
        </div>
        {/* Firma digitale */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mt-4" data-testid="signature-section">
          <div className="flex items-center gap-2 mb-3">
            <PenTool size={18} className="text-slate-700" />
            <h2 className="text-sm font-semibold text-slate-900">Firma digitale</h2>
          </div>

          {(() => {
            const signingAllowed = SIGNABLE_STATUSES.has(sub.status);
            const bothSigned = locSigned && condSigned;
            return !signingAllowed ? (
              <div
                className="border border-amber-300 bg-amber-50 rounded-md p-3 mb-4 flex items-start gap-2"
                data-testid="signature-locked-banner"
              >
                <Lock size={14} className="text-amber-800 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  <p className="font-semibold mb-0.5">In attesa di approvazione da Unioncasa Milano</p>
                  <p>
                    La firma del contratto sarà abilitata non appena l&apos;amministrazione
                    avrà verificato la pratica. Riceverai una notifica via email con l&apos;OK
                    per procedere alla firma del locatore e del conduttore.
                  </p>
                </div>
              </div>
            ) : !bothSigned ? (
              <div
                className="border border-indigo-300 bg-indigo-50 rounded-md p-3 mb-4 flex items-start gap-2"
                data-testid="signature-unlocked-banner"
              >
                <CheckCircle2 size={14} className="text-indigo-700 shrink-0 mt-0.5" />
                <div className="text-xs text-indigo-900 leading-relaxed">
                  <p className="font-semibold mb-0.5">Pratica approvata — puoi firmare il contratto</p>
                  <p>Procedi con la firma del locatore e del conduttore qui sotto.</p>
                </div>
              </div>
            ) : null;
          })()}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`p-2.5 rounded border ${locSigned ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`} data-testid="loc-sig-status">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Locatore</p>
              <p className={`text-sm mt-0.5 ${locSigned ? "text-emerald-800 font-medium" : "text-slate-700"}`}>
                {locSigned ? "✓ Firmato" : "Non ancora firmato"}
              </p>
            </div>
            <div className={`p-2.5 rounded border ${condSigned ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`} data-testid="cond-sig-status">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Conduttore</p>
              <p className={`text-sm mt-0.5 ${condSigned ? "text-emerald-800 font-medium" : "text-slate-700"}`}>
                {condSigned ? "✓ Firmato" : "Non ancora firmato"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5" data-testid="cond-sig-detail">
                Contratto: {condContrSigned ? "✓" : "—"} · Attestazione: {condAttSigned ? "✓" : "—"}
              </p>
            </div>
          </div>

          {(() => {
            const signingAllowed = SIGNABLE_STATUSES.has(sub.status);
            const lockedTitle = "In attesa dell'OK di Unioncasa Milano";
            return (
              <div className="flex flex-wrap gap-2">
                {!locSigned && (
                  <button
                    type="button"
                    onClick={() => signingAllowed && setSignLocOpen(true)}
                    disabled={!signingAllowed}
                    title={signingAllowed ? undefined : lockedTitle}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium ${
                      signingAllowed
                        ? "bg-slate-900 text-white hover:bg-black"
                        : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                    data-testid="btn-firma-locatore"
                  >
                    {signingAllowed ? <PenTool size={12} /> : <Lock size={12} />}
                    Firma tu ora
                  </button>
                )}
                {!condSigned && (
                  <button
                    type="button"
                    onClick={() => signingAllowed && generateConduttoreLink()}
                    disabled={!signingAllowed || genLinkLoading}
                    title={signingAllowed ? undefined : lockedTitle}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium ${
                      signingAllowed
                        ? "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                        : "border border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                    } disabled:opacity-60`}
                    data-testid="btn-link-conduttore"
                  >
                    {!signingAllowed && <Lock size={12} />}
                    {genLinkLoading ? "Generazione…" : "🔗 Invia link firma al conduttore"}
                  </button>
                )}
                {isAgenzia && (!locSigned || !condSigned) && (
                  <button
                    type="button"
                    onClick={() => signingAllowed && setSignAgOpen(true)}
                    disabled={!signingAllowed}
                    title={signingAllowed ? undefined : lockedTitle}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium ${
                      signingAllowed
                        ? "border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                        : "border border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                    }`}
                    data-testid="btn-firma-agenzia"
                  >
                    {signingAllowed ? <Building2 size={12} /> : <Lock size={12} />}
                    Firma in sede (entrambe le parti)
                  </button>
                )}
              </div>
            );
          })()}

          {conduttoreLink && (
            <div className="mt-3 p-2.5 border border-slate-200 rounded bg-slate-50" data-testid="conduttore-link-box">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Link firma conduttore (7 giorni)</p>
              <div className="flex items-center gap-2">
                <input readOnly value={conduttoreLink} className="flex-1 h-8 px-2 border border-slate-300 rounded bg-white text-xs" data-testid="conduttore-link-input" />
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-slate-900 text-white text-xs hover:bg-black"
                  data-testid="conduttore-link-copy"
                >
                  <Copy size={12} /> Copia
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Condividi questo link con il conduttore (email, WhatsApp, SMS…). Al click potrà firmare senza bisogno di account.
              </p>
            </div>
          )}
        </div>

        {/* Dialog: firma locatore */}
        <Dialog open={signLocOpen} onOpenChange={setSignLocOpen}>
          <DialogContent className="max-w-lg" data-testid="dialog-firma-locatore">
            <DialogHeader>
              <DialogTitle>Firma Locatore</DialogTitle>
            </DialogHeader>
            <SignaturePad label="La tua firma" value={locSig} onChange={setLocSig} testId="loc-sig-pad" />
            <DialogFooter>
              <button type="button" onClick={() => setSignLocOpen(false)} className="px-3 py-1.5 rounded border border-slate-300 text-xs">Annulla</button>
              <button
                type="button"
                onClick={signLocatore}
                disabled={signing || !locSig}
                className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs disabled:opacity-50"
                data-testid="btn-firma-locatore-confirm"
              >
                {signing ? "Salvataggio…" : "Conferma firma"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: firma in agenzia (locatore + conduttore) */}
        <Dialog open={signAgOpen} onOpenChange={setSignAgOpen}>
          <DialogContent className="max-w-2xl" data-testid="dialog-firma-agenzia">
            <DialogHeader>
              <DialogTitle>🏢 Firma in sede — Locatore e Conduttore</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-600 mb-2">
              Passa il dispositivo al locatore per la prima firma, poi al conduttore. Entrambe le firme
              vengono acquisite in questa sessione e salvate contestualmente.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <SignaturePad label="Firma Locatore" value={agLocSig} onChange={setAgLocSig} testId="ag-loc-pad" />
              <SignaturePad label="Firma Conduttore" value={agCondSig} onChange={setAgCondSig} testId="ag-cond-pad" />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setSignAgOpen(false)} className="px-3 py-1.5 rounded border border-slate-300 text-xs">Annulla</button>
              <button
                type="button"
                onClick={signAgenzia}
                disabled={signing || !agLocSig || !agCondSig}
                className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs disabled:opacity-50"
                data-testid="btn-firma-agenzia-confirm"
              >
                {signing ? "Salvataggio…" : "Conferma entrambe le firme"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
